import React from 'react';
import styled, { css } from 'styled-components';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { colors, spaces, typography } from '../../../lib/styles';

interface Props {
    currentPage: number;
    totalPages: number;
    onClick: (page: number) => void;
    displayMargin?: number;
}

const StyledPaginationList = styled.ul`
    margin: 0;
    padding: 0;
`;

const ListItem = styled.li`
    display: inline-block;
    margin: 0;

    &:first-of-type {
        margin-left: 0;
    }

    &:last-of-type {
        margin-right: 0;
    }
`;

const PageNumberButton = styled.button<{ disabled?: boolean }>(({ disabled }) => {
    return css`
        border: none;
        font-family: ${typography.fontFamilies.heading};
        font-size: 19px;
        color: ${colors.grey800};
        background-color: transparent;
        min-width: 44px;
        min-height: 44px;
        padding: 0;

        :hover {
            &:after {
                border-bottom-color: ${!disabled && colors.grey700};
            }
        }

        :focus {
            outline: thin dotted ${colors.grey700};
        }

        &:after {
            content: '';
            display: block;
            width: 35%;
            margin: 0 auto;
            padding-top: ${spaces.space4}px;
            border-bottom-width: 2px;
            border-bottom-style: solid;
            border-bottom-color: ${disabled ? colors.grey800 : 'transparent'};
            transition: border-bottom-color 0.15s;
        }
    `;
});

const Placeholder = styled.span`
    color: ${colors.grey800};
`;

export default function PaginationList({
    currentPage,
    totalPages,
    onClick,
    displayMargin = 2,
}: Props) {
    const pageNumbers: JSX.Element[] = [];
    const pageDisplayLimit = 6;
    const isOverPageDisplayLimit = totalPages > pageDisplayLimit;

    for (let page = 1; page <= totalPages; page++) {
        const isFirstPage = page === 1;
        const isLastPage = page === totalPages;
        const isCurrentPage = page === currentPage;

        const firstSkippedRange = isOverPageDisplayLimit ? currentPage - displayMargin : 0;
        const lastSkippedRange = isOverPageDisplayLimit ? currentPage + displayMargin : totalPages;
        const isFirstSkippedRange = page === firstSkippedRange;
        const isLastSkippedRange = page === lastSkippedRange;
        const isInRange = page >= firstSkippedRange && page <= lastSkippedRange;
        const isGap = (isFirstSkippedRange || isLastSkippedRange) && !(isFirstPage || isLastPage);

        const isVisible =
            isFirstPage ||
            isLastPage ||
            isCurrentPage ||
            isInRange ||
            isFirstSkippedRange ||
            isLastSkippedRange;

        if (!isVisible) {
            continue;
        }

        pageNumbers.push(
            <ListItem key={page}>
                {isGap ? (
                    <Placeholder>…</Placeholder>
                ) : (
                    <PageNumberButton
                        key={page}
                        onClick={() => {
                            onClick(page);
                        }}
                        data-testid={'page-number'}
                        aria-label={
                            isCurrentPage
                                ? formatMessage(__('Current page, page {pageNumber}'), {
                                      pageNumber: page,
                                  })
                                : formatMessage(__('Go to page {pageNumber}'), {
                                      pageNumber: page,
                                  })
                        }
                        disabled={isCurrentPage}
                        aria-disabled={isCurrentPage}
                        aria-current={isCurrentPage}
                    >
                        {page}
                    </PageNumberButton>
                )}
            </ListItem>
        );
    }

    return <StyledPaginationList>{pageNumbers}</StyledPaginationList>;
}
